// src/components/Slider.js
import React, { useEffect, useRef, useState } from 'react';
import { applyImageFallback, getImageAssetUrl } from '../utils/imageAssets';

const intervalDuration = 5000;

const slides = [
  {
    kicker: 'PRIVATE SALE',
    title: 'Private sale и выгодные находки',
    text: 'Следим за закрытыми sale, лимитированными позициями и выгодными предложениями.',
    image: '/images/slider/promo-private-sale.webp',
    fallbackImage: '/images/slider/promo-private-sale.png',
  },
  {
    kicker: 'ДОСТАВКА',
    title: 'Доставка по всей России',
    text: 'Отправляем по всей России удобными службами доставки и подберем оптимальный вариант получения.',
    image: '/images/slider/promo-delivery.webp',
    fallbackImage: '/images/slider/promo-delivery.png',
  },
  {
    kicker: 'CONCIERGE',
    title: 'Сопровождение заказа',
    text: 'Расчет, выкуп, статусы и уведомления — всё в одном процессе.',
    image: '/images/slider/promo-concierge.webp',
    fallbackImage: '/images/slider/promo-concierge.png',
  },
];

const Slider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const pauseSlider = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const resumeSlider = () => {
    if (intervalRef.current) return;

    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, intervalDuration);
  };

  useEffect(() => {
    resumeSlider();
    return () => pauseSlider();
  }, []);

  const handleTouchStart = (event) => {
    pauseSlider();
    touchStartX.current = event.touches[0].clientX;
    touchEndX.current = event.touches[0].clientX;
  };

  const handleTouchMove = (event) => {
    touchEndX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const deltaX = touchStartX.current - touchEndX.current;

    if (Math.abs(deltaX) > 40) {
      if (deltaX > 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }

    resumeSlider();
  };

  return (
    <section className="container promo-slider-section">
      <div
        className="promo-slider"
        onMouseEnter={pauseSlider}
        onMouseLeave={resumeSlider}
        onMouseDown={pauseSlider}
        onMouseUp={resumeSlider}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={resumeSlider}
      >
        <button
          className="promo-slider-arrow promo-slider-arrow-prev"
          type="button"
          onClick={() => {
            pauseSlider();
            goToPrevious();
            setTimeout(resumeSlider, 300);
          }}
          aria-label="Предыдущий слайд"
        >
          ‹
        </button>

        <div className="promo-slider-window">
          {slides.map((slide, index) => (
            <article
              className={`promo-slide promo-slide-${index} ${index === currentSlide ? 'active' : ''}`}
              key={slide.title}
              aria-hidden={index !== currentSlide}
            >
              <div className="promo-slide-text">
                <p className="promo-slide-kicker">{slide.kicker}</p>
                <h2>{slide.title}</h2>
                <p className="promo-slide-description">{slide.text}</p>
              </div>

              <div className="promo-slide-visual">
                <img
                  src={getImageAssetUrl(slide.image)}
                  alt=""
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  onError={(event) => applyImageFallback(event, slide.fallbackImage)}
                />
              </div>
            </article>
          ))}
        </div>

        <button
          className="promo-slider-arrow promo-slider-arrow-next"
          type="button"
          onClick={() => {
            pauseSlider();
            goToNext();
            setTimeout(resumeSlider, 300);
          }}
          aria-label="Следующий слайд"
        >
          ›
        </button>
      </div>

      <div className="promo-slider-bullets">
        {slides.map((slide, index) => (
          <button
            type="button"
            key={slide.title}
            className={`promo-slider-bullet ${index === currentSlide ? 'active' : ''}`}
            onClick={() => {
              pauseSlider();
              setCurrentSlide(index);
              setTimeout(resumeSlider, 300);
            }}
            aria-label={`Перейти к слайду ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Slider;
